import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { SessionProvider, useSession } from './lib/session.js';
import { Shell } from './components/Shell.js';
import { SignIn } from './screens/SignIn.js';
import { ChangePassword } from './screens/ChangePassword.js';
import { AcceptInvite } from './screens/AcceptInvite.js';
import { Branding } from './screens/Branding.js';
import { Fleet } from './screens/Fleet.js';
import { Network } from './screens/Network.js';
import { Timetables } from './screens/Timetables.js';
import { Trips } from './screens/Trips.js';
import { Disputes } from './screens/Disputes.js';
import { Promotions } from './screens/Promotions.js';
import { Dashboard } from './screens/Dashboard.js';
import { Bookings } from './screens/Bookings.js';
import { Staff } from './screens/Staff.js';
import { Roles } from './screens/Roles.js';
import { Profile } from './screens/Profile.js';
import { Settings } from './screens/Settings.js';
import { Billing } from './screens/Billing.js';
import { Routes as RoutePerformance } from './screens/Routes.js';

function Authenticated() {
  const { user, tenant } = useSession();
  if (!user || !tenant) return null;

  return (
    <Shell>
      {tenant.status === 'SUSPENDED' && (
        <div
          className="px-8 py-2.5 text-sm font-medium"
          style={{ background: 'var(--signal-cancelled)', color: '#fff' }}
          role="status"
        >
          Account suspended — contact platform support. The dashboard is read-only.
        </div>
      )}
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/branding" element={<Branding tenantSlug={tenant.slug} />} />
        <Route path="/routes" element={<RoutePerformance />} />
            <Route path="/trips" element={<Trips />} />
        <Route path="/bookings" element={<Bookings />} />
        <Route path="/disputes" element={<Disputes />} />
        <Route path="/promotions" element={<Promotions />} />
        <Route path="/network" element={<Network />} />
        <Route path="/fleet" element={<Fleet />} />
        <Route path="/timetables" element={<Timetables />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/staff" element={<Staff />} />
        <Route path="/roles" element={<Roles />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

function Gate() {
  const { user, loading, mustChangePassword } = useSession();

  // An invitation link arrives before any session exists, so it is handled
  // ahead of the sign-in screen.
  const inviteToken = new URLSearchParams(window.location.search).get('token');
  if (!user && inviteToken && window.location.pathname.includes('accept-invite')) {
    return (
      <AcceptInvite
        token={inviteToken}
        onAccepted={() => {
          window.location.replace('/');
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center">
        <span
          role="status"
          aria-label="Loading"
          className="h-6 w-6 animate-spin rounded-full border-2 border-black/15 border-t-black/45"
        />
      </div>
    );
  }
  // Before <Authenticated />, because that screen loads the tenant and every panel behind it, and
  // on a temporary-password session all of it is refused.
  if (mustChangePassword) return <ChangePassword />;

  return user ? <Authenticated /> : <SignIn />;
}

export function App() {
  return (
    <SessionProvider>
      <BrowserRouter>
        <Gate />
      </BrowserRouter>
    </SessionProvider>
  );
}
